import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Input } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import axios from "axios";
import useLang from "../../../hooks/useLang";
import {
	AddOrderStatusHistoryRequest,
	OrderStatusHistoryOverview,
	OrderStatusHistoryRow,
	ResponsibleUser,
	addOrderStatusHistory,
	deleteOrderStatusHistory,
	getOrderStatusHistory,
	getResponsibleUsers,
} from "../../../api/bulk-order-operations";
import { getCategoryConfig } from "../../../utils/bulkOperationsConfig";
import { parseOrderIds } from "../parseOrderIds";
import BulkOpsHeader from "../components/BulkOpsHeader";
import { PRIMARY_BUTTON_STYLE } from "../components/styles";
import AddHistoryModal from "./AddHistoryModal";
import HistoryTable from "./HistoryTable";
import OrderSummary from "./OrderSummary";
import DeleteHistoryModal from "./DeleteHistoryModal";

const HistoryManagement = () => {
	const { strings } = useLang("bulkOrderOperations");
	const common = strings?.common;
	const copy = strings?.historyManagement;
	const categoryStrings = strings?.categories?.historyManagement;

	const config = getCategoryConfig("history-management");

	const [rawInput, setRawInput] = useState("");
	const [loading, setLoading] = useState(false);
	const [overview, setOverview] = useState<OrderStatusHistoryOverview | null>(
		null
	);

	const [users, setUsers] = useState<ResponsibleUser[]>([]);
	const [usersLoading, setUsersLoading] = useState(false);

	const [addOpen, setAddOpen] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [rowToDelete, setRowToDelete] =
		useState<OrderStatusHistoryRow | null>(null);
	const [deleting, setDeleting] = useState(false);

	// Same parser as the AMK screen, so "DF32-123123" works here too.
	const parsed = parseOrderIds(rawInput);
	const orderId = parsed.valid.length === 1 ? parsed.valid[0] : null;

	const load = useCallback(async (id: number) => {
		setLoading(true);
		try {
			setOverview(await getOrderStatusHistory(id));
		} catch {
			// The axios interceptor already toasted the API's message.
			setOverview(null);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		let cancelled = false;
		setUsersLoading(true);
		getResponsibleUsers()
			.then((list) => {
				if (!cancelled) setUsers(list);
			})
			.catch(() => undefined)
			.finally(() => {
				if (!cancelled) setUsersLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const handleAdd = async (body: AddOrderStatusHistoryRequest) => {
		setSubmitting(true);
		try {
			await addOrderStatusHistory(body);
			toast.success(copy?.addSuccess ?? "History row added");
			setAddOpen(false);
			await load(body.orderId);
		} catch (err) {
			if (!axios.isAxiosError(err)) throw err;
			// Interceptor toasted it; keep the modal open so the input isn't lost.
		} finally {
			setSubmitting(false);
		}
	};

	const handleDelete = async () => {
		if (!rowToDelete || !overview) return;
		setDeleting(true);
		try {
			await deleteOrderStatusHistory(rowToDelete.id);
			toast.success(copy?.deleteSuccess ?? "History row deleted");
			setRowToDelete(null);
			await load(overview.orderId);
		} catch (err) {
			if (!axios.isAxiosError(err)) throw err;
		} finally {
			setDeleting(false);
		}
	};

	return (
		<div className="max-w-5xl mx-auto px-4 py-6 flex flex-col gap-6">
			<BulkOpsHeader
				title={categoryStrings?.label ?? "History Management"}
				subtitle={
					categoryStrings?.description ??
					"Correct an order's status history"
				}
				backTo="/bulk-order-operations"
				backLabel={common?.back ?? "All categories"}
			/>

			{!config && (
				<Alert
					type="error"
					showIcon
					message={
						common?.categoryUnavailable ??
						"This category is not configured for this country."
					}
				/>
			)}

			<div className="flex flex-wrap items-end gap-2">
				<div className="flex-1 min-w-[240px]">
					<div className="font-medium mb-2">
						{copy?.orderIdLabel ?? "Order ID"}
					</div>
					<Input
						className="font-mono"
						value={rawInput}
						onChange={(e) => setRawInput(e.target.value)}
						onPressEnter={() => orderId && load(orderId)}
						placeholder={
							copy?.orderIdPlaceholder ??
							"e.g. 4003935 or DF32-123123"
						}
					/>
				</div>
				<Button
					size="large"
					style={PRIMARY_BUTTON_STYLE}
					loading={loading}
					disabled={!orderId || !config}
					onClick={() => orderId && load(orderId)}
				>
					{copy?.continue ?? "Continue"}
				</Button>
			</div>

			{rawInput.trim() && !orderId && (
				<Alert
					type="warning"
					showIcon
					message={
						copy?.singleOrderOnly ??
						"Enter exactly one valid order id."
					}
				/>
			)}

			{overview && config && (
				<>
					<OrderSummary overview={overview} />

					<div className="flex items-center justify-between gap-2">
						<h2 className="text-lg font-semibold">
							{copy?.historyTitle ?? "Status history"}
						</h2>
						<Button
							icon={<PlusOutlined />}
							style={PRIMARY_BUTTON_STYLE}
							onClick={() => setAddOpen(true)}
						>
							{copy?.add ?? "Add"}
						</Button>
					</div>

					<HistoryTable
						rows={overview.rows}
						loading={loading}
						onDelete={setRowToDelete}
					/>

					<AddHistoryModal
						open={addOpen}
						orderId={overview.orderId}
						isInternational={overview.isInternational}
						users={users}
						usersLoading={usersLoading}
						config={config}
						submitting={submitting}
						onCancel={() => setAddOpen(false)}
						onSubmit={handleAdd}
					/>

					<DeleteHistoryModal
						row={rowToDelete}
						deleting={deleting}
						onCancel={() => setRowToDelete(null)}
						onConfirm={handleDelete}
					/>
				</>
			)}
		</div>
	);
};

export default HistoryManagement;
